import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { GlobalButton } from './GlobalComponents/GlobalButton';

interface UnsavedChangesModalProps {
  open: boolean;
  onClose: () => void;         // "Keep editing" — stay on page
  onDiscard: () => void;       // "Discard" — leave without saving
  onSave?: () => void;         // Optional: "Save & Leave"
  saving?: boolean;
  title?: string;
  subtitle?: string;
}

const UnsavedChangesModal: React.FC<UnsavedChangesModalProps> = ({
  open,
  onClose,
  onDiscard,
  onSave,
  saving = false,
  title = 'Unsaved Changes',
  subtitle = 'You have unsaved changes. If you leave now, your changes will be lost.',
}) => {
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isClosing) {
      document.querySelector('.unsaved-changes-modal .p-dialog')?.classList.add('dialog-closing');
      document.querySelector('.unsaved-changes-modal')?.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    if (saving) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  const handleDiscard = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onDiscard();
    }, 400);
  };

  const handleSave = () => {
    if (onSave) onSave();
  };

  return (
    <>
      <style>{`
        .unsaved-changes-modal {
          backdrop-filter: blur(4px);
          background-color: rgba(0,0,0,0.25) !important;
          animation: ucmMaskIn 0.35s ease;
          z-index: 9999 !important;
        }
        .unsaved-changes-modal.mask-closing {
          animation: ucmMaskOut 0.4s ease !important;
        }
        @keyframes ucmMaskIn  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes ucmMaskOut { from { opacity: 1; } to { opacity: 0; } }

        .unsaved-changes-modal .p-dialog {
          animation: ucmDialogIn 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: none !important;
          background: transparent !important;
          border: none !important;
          overflow: visible !important;
        }
        .unsaved-changes-modal .p-dialog::before {
          content: '';
          position: absolute;
          inset: -15px;
          background: rgba(255, 255, 255, 0.64);
          border-radius: 40px;
          z-index: -1;
          backdrop-filter: blur(20px);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        }
        .unsaved-changes-modal .p-dialog.dialog-closing {
          animation: ucmDialogOut 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        @keyframes ucmDialogIn {
          from { opacity: 0; transform: scale(0.92) translateY(-20px); }
          to   { opacity: 1; transform: scale(1)    translateY(0); }
        }
        @keyframes ucmDialogOut {
          from { opacity: 1; transform: scale(1)    translateY(0); }
          to   { opacity: 0; transform: scale(0.88) translateY(25px); }
        }

        .ucm-change-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: #fffbeb;
          border: 1px solid #fde68a;
          border-radius: 8px;
          font-size: 13px;
          color: #92400e;
          font-weight: 500;
        }
      `}</style>

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        showHeader={false}
        style={{
          width: '90vw',
          maxWidth: '480px',
          borderRadius: '32px',
          overflow: 'visible',
          boxShadow: 'none',
        }}
        contentStyle={{
          padding: '32px',
          background: 'white',
          borderRadius: '20px',
          boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
        }}
        modal
        dismissableMask={!saving}
        draggable={false}
        resizable={false}
        blockScroll
        maskClassName="unsaved-changes-modal"
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          disabled={saving}
          style={{
            position: 'absolute', top: '20px', right: '20px',
            background: 'none', border: 'none', fontSize: '22px',
            cursor: saving ? 'not-allowed' : 'pointer', color: '#6b7280',
            width: '32px', height: '32px', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            transition: 'color 0.2s', padding: 0, zIndex: 10,
          }}
          onMouseOver={e => (e.currentTarget.style.color = '#1f2937')}
          onMouseOut={e => (e.currentTarget.style.color = '#6b7280')}
        >×</button>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '16px',
          marginBottom: '20px', paddingBottom: '20px',
          borderBottom: '1px solid #e5e7eb',
        }}>
          <div style={{
            width: '54px', height: '54px', borderRadius: '14px', flexShrink: 0,
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(245,158,11,0.35)', border: '3px solid white',
          }}>
            <i className="pi pi-exclamation-triangle" style={{ color: 'white', fontSize: '22px' }} />
          </div>
          <div>
            <div style={{
              fontSize: '20px', fontWeight: 700, color: '#1e40af',
              lineHeight: 1.2, marginBottom: '4px',
            }}>
              {title}
            </div>
            <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
              {subtitle}
            </div>
          </div>
        </div>

        {/* Warning detail */}
        <div className="ucm-change-item" style={{ marginBottom: '24px' }}>
          <i className="pi pi-info-circle" style={{ fontSize: '14px', flexShrink: 0 }} />
          <span>Your letterhead edits have not been saved yet.</span>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {onSave && (
            <GlobalButton
              label={saving ? 'Saving…' : 'Save & Continue'}
              onClick={handleSave}
              variant="primary"
              size="medium"
              fullWidth
              loading={saving}
              disabled={saving}
            />
          )}
          <div style={{ display: 'flex', gap: '10px' }}>
            <GlobalButton
              label="Keep Editing"
              onClick={handleClose}
              variant="outline"
              size="medium"
              fullWidth
              disabled={saving}
            />
            <GlobalButton
              label="Discard Changes"
              onClick={handleDiscard}
              variant="danger"
              size="medium"
              fullWidth
              disabled={saving}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default UnsavedChangesModal;